/**
 * Дашборд объекта ОСНОВА.Руководитель — зеркало DTO
 * `/api/v1/agents/progress/objects/*` (snake_case 1:1 с backend serde,
 * `osnova-progress/src/dashboard`).
 */

import type { ProgressKpi } from './types';

/** Объект в пикере дашборда. */
export interface ProgressObjectItem {
  id: string;
  name: string;
  code: string | null;
  status: string;
  customer_name: string | null;
  /** Привязки объекта сохранены руководителем. */
  configured: boolean;
}

export interface DashObject {
  id: string;
  name: string;
  code: string | null;
  status: string;
}

/** Действующие привязки объекта (что учтено в цифрах). */
export interface DashProfileSummary {
  saved: boolean;
  /** Часть привязок (или все) подобрана автоматически из ПТО и Диспетчера. */
  auto_filled: boolean;
  customer_name: string | null;
  customer_domains: string[];
  customer_aliases: string[];
  letter_markers: string[];
  contract_estimate_id: string | null;
  meetings_series_id: string | null;
}

export interface MonthLetters {
  month: string;
  incoming: number;
  outgoing: number;
}

export interface LetterBrief {
  id: string;
  direction: 'incoming' | 'outgoing' | string;
  at: string;
  counterpart: string;
  subject: string;
  awaiting_answer: boolean;
  answer_overdue: boolean;
}

export interface CorrespondenceBlock {
  configured: boolean;
  incoming_total: number;
  outgoing_total: number;
  incoming_recent: number;
  outgoing_recent: number;
  awaiting_answer: number;
  answer_overdue: number;
  last_incoming_at: string | null;
  last_outgoing_at: string | null;
  months: MonthLetters[];
  recent: LetterBrief[];
  truncated: boolean;
  /** Список писем скрыт: нет доступа к почте (Диспетчер/Юрист). */
  recent_hidden: boolean;
}

export interface ClaimBrief {
  id: string;
  claim_type: 'claim' | 'lawsuit' | string;
  direction: 'incoming' | 'outgoing' | string;
  counterparty: string;
  stage: string;
  amount: number | null;
  created_at: string;
  answered: boolean;
  response_due_at: string | null;
  response_overdue: boolean;
}

export interface ClaimsBlock {
  configured: boolean;
  received: number;
  answered: number;
  awaiting: number;
  overdue: number;
  received_amount: number;
  sent: number;
  lawsuits: number;
  recent: ClaimBrief[];
  /** Список претензий скрыт: нет доступа к ОСНОВА.Юрист. */
  recent_hidden: boolean;
}

export interface UnitProgress {
  unit: string;
  lines: number;
  fo_total: number;
  fact_cum: number;
  pa_accepted: number;
  nzp: number;
  completion_pct: number | null;
  billed_pct: number | null;
}

export interface KfoProgressBlock {
  available: boolean;
  report_date: string | null;
  lines: number;
  lines_with_plan: number;
  lines_completed: number;
  lines_started: number;
  completion_pct: number | null;
  lines_absent: number;
  units: UnitProgress[];
}

export interface LedgerBrief {
  id: string;
  contract_no: string;
  object_name: string;
  title: string | null;
  total_cost: number;
  pa_count: number;
  last_period: string | null;
}

export interface MonthSum {
  month: string;
  total: number;
}

export interface BillingBlock {
  kfo_available: boolean;
  lines_with_fact: number;
  lines_fully_billed: number;
  lines_with_nzp: number;
  billed_lines_pct: number | null;
  ledgers: LedgerBrief[];
  ledgers_auto: boolean;
  billed_total: number;
  last_period: string | null;
  last_period_total: number;
  months: MonthSum[];
}

export interface KspBlock {
  connected: boolean;
  note: string;
  snapshot: import('@entities/ksp/types').KspPublishedSnapshot | null;
  operational: import('@entities/ksp/types').KspSummaryEnvelope['operational'] | null;
}

export interface OtherBlock {
  kpis: ProgressKpi[];
  warnings: string[];
}

/** Площадка Хронума с её сегодняшними числами. */
export interface AttendanceSite {
  id: string;
  name: string;
  /** На смене прямо сейчас (забытые уходы сюда не входят). */
  on_shift_now: number;
  /** Закреплено за площадкой в карточках работников. */
  workers_attached: number;
  /** Отметились за сегодняшние сутки площадки, включая ушедших. */
  attended_today: number;
  /** Смены с прошлых суток, где уход забыли. */
  open_shifts_stale: number;
  /** Местное время площадки со смещением зоны. */
  local_time: string | null;
  local_date: string | null;
  /** Пояс площадки (IANA): по нему Хронум считает её сутки. */
  timezone: string | null;
  companies: string[];
}

/** Итоги Хронума по холдингу — отдельный пункт выбора в баннере. */
export interface AttendanceHolding {
  workers: number;
  on_shift_now: number;
  attended_today: number;
  open_shifts_stale: number;
  companies: number;
  sites: number;
}

/**
 * Ответ `GET /agents/progress/chronum` — люди на объекте по данным Хронума.
 * Площадки приходят ВСЕ, какие есть у холдинга: в баннере выбирается любая,
 * даже если объекта ОСНОВЫ под неё не заведено.
 */
export interface AttendanceView {
  /** Интеграция настроена (в окружении есть ключ Хронума). */
  connected: boolean;
  /** Числа получены: Хронум ответил и площадки у холдинга есть. */
  available: boolean;
  note: string;
  /** Площадки объекта подобраны по его названию, а не выбраны руками. */
  auto_matched: boolean;
  /** Площадки открытого объекта ОСНОВЫ: с них баннер открывается. */
  object_site_ids: string[];
  /** Все площадки холдинга в Хронуме. */
  sites: AttendanceSite[];
  holding: AttendanceHolding | null;
  source_ref: string;
  updated_at: string | null;
}

/** Площадка Хронума в форме привязок объекта. */
export interface ChronumSiteOption {
  id: string;
  name: string;
  companies: string[];
  on_shift_now: number;
  workers_attached: number;
  /** Подобрана автоматически по названию и коду объекта. */
  auto: boolean;
  /** Учитывается сейчас. */
  selected: boolean;
}

/** Ответ `GET /agents/progress/objects/{id}/dashboard`. */
export interface ObjectDashboard {
  object: DashObject;
  profile: DashProfileSummary;
  contracts: string[];
  correspondence: CorrespondenceBlock;
  claims: ClaimsBlock;
  progress: KfoProgressBlock;
  billing: BillingBlock;
  ksp: KspBlock;
  other: OtherBlock;
  warnings: string[];
  generated_at: string;
}

/** Сохранённые привязки объекта. */
export interface ObjectProfile {
  project_id: string;
  customer_name: string | null;
  customer_domains: string[];
  customer_aliases: string[];
  letter_markers: string[];
  /** `null` — ленты ПА подбираются по номерам договоров объекта. */
  accum_ledger_ids: string[] | null;
  contract_estimate_id: string | null;
  meetings_series_id: string | null;
  /** Площадки Хронума объекта. Пусто — подбираются по названию и коду. */
  chronum_site_ids: string[];
  updated_by: string | null;
  updated_at: string;
}

export interface LedgerOption {
  id: string;
  contract_no: string;
  object_name: string;
  title: string | null;
  total_cost: number;
  last_period: string | null;
  /** Подбирается автоматически по номеру договора объекта. */
  auto: boolean;
  /** Учитывается сейчас. */
  selected: boolean;
}

/** Привязки, подобранные автоматически из ПТО и Диспетчера. */
export interface AutoProfile {
  customer_name: string | null;
  customer_domains: string[];
  customer_aliases: string[];
  letter_markers: string[];
  contract_estimate_id: string | null;
  /** У Заказчика несколько объектов — письма разводятся маркерами. */
  shared_customer: boolean;
}

/** Ответ `GET /agents/progress/objects/{id}/profile`. */
export interface ObjectProfileView {
  object: DashObject;
  profile: ObjectProfile | null;
  contract_numbers: string[];
  ledgers: LedgerOption[];
  ledgers_auto: boolean;
  /** Площадки Хронума: что можно привязать. Пусто — интеграция не настроена. */
  chronum_sites: ChronumSiteOption[];
  /** Площадки сейчас подобраны автоматически. */
  chronum_sites_auto: boolean;
  /** Подобрано автоматически — подставляется в пустые поля формы. */
  auto: AutoProfile;
}

/** Тело `PUT /agents/progress/objects/{id}/profile`. */
export interface ObjectProfileUpdate {
  customer_name: string | null;
  customer_domains: string[];
  customer_aliases: string[];
  letter_markers: string[];
  accum_ledger_ids: string[] | null;
  contract_estimate_id: string | null;
  meetings_series_id: string | null;
  chronum_site_ids: string[];
}

/* ── Форматирование для дашборда ───────────────────────────────────────────── */

const MONTHS_SHORT = [
  'янв',
  'фев',
  'мар',
  'апр',
  'май',
  'июн',
  'июл',
  'авг',
  'сен',
  'окт',
  'ноя',
  'дек',
];

/** `2026-08` → `авг 2026` (`withYear=false` → `авг`). */
export function fmtMonth(key: string | null | undefined, withYear = true): string {
  if (!key) return '—';
  const [y, m] = key.split('-');
  const idx = Number(m) - 1;
  if (!y || Number.isNaN(idx) || idx < 0 || idx > 11) return key;
  return withYear ? `${MONTHS_SHORT[idx]} ${y}` : MONTHS_SHORT[idx];
}

/** Неразрывный пробел: «1,17 млрд ₽» не разрывается на две строки в плитке. */
const NBSP = '\u00A0';

/** Рубли крупно: `1 174,5 млн ₽` / `310 тыс ₽` / `950 ₽`. */
export function fmtRub(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  const abs = Math.abs(value);
  const num = (v: number, digits: number) =>
    v.toLocaleString('ru-RU', { maximumFractionDigits: digits });
  if (abs >= 1e9) return `${num(value / 1e9, 2)}${NBSP}млрд${NBSP}₽`;
  if (abs >= 1e6) return `${num(value / 1e6, 1)}${NBSP}млн${NBSP}₽`;
  if (abs >= 1e3) return `${num(value / 1e3, 0)}${NBSP}тыс${NBSP}₽`;
  return `${num(value, 0)}${NBSP}₽`;
}

/** Процент без знака: `84,7 %`. */
export function fmtShare(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return `${value.toLocaleString('ru-RU', { maximumFractionDigits: 1 })}${NBSP}%`;
}

/** Объём крупно: `1,59 млн` / `815 338` / `12,5`. */
export function fmtQty(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  const abs = Math.abs(value);
  if (abs >= 1e6)
    return `${(value / 1e6).toLocaleString('ru-RU', { maximumFractionDigits: 2 })}${NBSP}млн`;
  return value.toLocaleString('ru-RU', { maximumFractionDigits: abs >= 100 ? 0 : 1 });
}
